/**
 * Plain words for "where did this customer come from?".
 *
 * The server stores the website's answer as `came_from` (with a ready-made
 * `label`, see src/server/services/cameFrom.ts) and the office's answer as
 * `heard_about` on the customer. This file turns either into one short line
 * for a chip. Staff never see the stored codes.
 */

/** Mirrors HEARD_ABOUT in src/server/services/cameFrom.ts and migration 711. */
export const HEARD_ABOUT_OPTIONS = [
    { value: "google_search", label: "Google search" },
    { value: "google_maps", label: "Google Maps" },
    { value: "google_ads", label: "Google ad" },
    { value: "city_notice", label: "City notice or letter" },
    { value: "referral", label: "Referral" },
    { value: "trade_partner", label: "Plumber or fire company" },
    { value: "returning", label: "Returning customer" },
    { value: "sign_truck", label: "Sign or truck" },
    { value: "other", label: "Other" },
] as const;

export type HeardAbout = (typeof HEARD_ABOUT_OPTIONS)[number]["value"];

export function heardAboutLabel(value: string | null | undefined): string | null {
    if (!value) return null;
    return HEARD_ABOUT_OPTIONS.find((o) => o.value === value)?.label ?? null;
}

/** The detail box changes its question to match the pick. */
export function heardAboutDetailPrompt(value: HeardAbout | ""): { label: string; placeholder: string } | null {
    switch (value) {
        case "referral":
            return { label: "Who referred them?", placeholder: "Name, e.g. their neighbor Pat Smith" };
        case "trade_partner":
            return { label: "Which company?", placeholder: "e.g. ABC Plumbing" };
        case "city_notice":
            return { label: "Which city? (optional)", placeholder: "e.g. Keller" };
        case "google_search":
        case "google_ads":
            return { label: "What did they search for? (optional)", placeholder: "e.g. backflow test Fort Worth" };
        case "other":
            return { label: "What was it?", placeholder: "e.g. Nextdoor post, trade show" };
        case "google_maps":
        case "returning":
        case "sign_truck":
            return { label: "Anything else? (optional)", placeholder: "" };
        default:
            return null;
    }
}

export interface CameFromRecord {
    label?: string | null;
    landing_page?: string | null;
    referrer?: string | null;
    utm_campaign?: string | null;
}

export interface CameFromSubject {
    came_from?: CameFromRecord | null;
    heard_about?: string | null;
    heard_about_detail?: string | null;
}

export interface CameFromChipText {
    /** "Came from: Google Ads · 'backflow test fort worth'" */
    text: string;
    /** Longer hover text: landing page, referring site, detail. */
    title: string;
}

/**
 * The office's own answer wins over the website's guess: if they asked the
 * caller, that's what the customer told us.
 */
export function cameFromChip(subject: CameFromSubject | null | undefined): CameFromChipText | null {
    if (!subject) return null;
    const heard = heardAboutLabel(subject.heard_about);
    if (heard) {
        const detail = subject.heard_about_detail?.trim();
        return {
            text: `Heard about us: ${heard}${detail ? ` · ${detail}` : ""}`,
            title: "What the customer told the office",
        };
    }
    const cf = subject.came_from;
    if (cf?.label) {
        const bits = [
            cf.landing_page ? `First page: ${cf.landing_page}` : null,
            cf.referrer ? `Sent by: ${cf.referrer}` : null,
        ].filter(Boolean);
        return {
            text: `Came from: ${cf.label}`,
            title: bits.length ? bits.join("\n") : "What the website saw when they booked",
        };
    }
    return null;
}
